import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { useUrlParamMirror } from "@/shared/hooks/useUrlParamMirror";
import { HttpApiService } from "@/shared/services/httpApi.service";

import { type ListPaymentMovementsParams, PaymentsWalletsService } from "../services/paymentsWallets.service";
import type { PaymentMovement } from "../types/payments-wallets.types";
import {
  EMPTY_MOVEMENTS_FILTERS,
  MOVEMENTS_URL_PARAMS,
  movementsQuery,
  readMovementsUrl,
  type WalletMovementsFilters,
} from "../utils/walletMovements";

export const WALLET_MOVEMENTS_PAGE_SIZE = 25;
const TYPING_DEBOUNCE_MS = 350;

export interface UseWalletMovementsResult {
  filters: WalletMovementsFilters;
  /** Changes some filters and goes back to the first page. */
  setFilters: (patch: Partial<WalletMovementsFilters>) => void;
  /** Replaces every filter at once (a preset), back to the first page. */
  replaceFilters: (next: WalletMovementsFilters) => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
  page: number;
  setPage: (page: number) => void;
  rows: PaymentMovement[];
  total: number;
  isLoading: boolean;
  /** A page has been read at least once. */
  isLoaded: boolean;
  error: string | null;
  /** False after a 401/403 — the caller renders the no-access view. */
  hasAccess: boolean;
  refresh: () => void;
}

/**
 * The staff movements list (`GET /internal/payments/movements`). Filters live in the URL under `m_`, read
 * once on mount; typing is debounced and every read is last-wins, so a slow answer never replaces a newer
 * one. `enabled` defers the first read until the tab is opened.
 */
export function useWalletMovements(
  enabled: boolean,
  includeColoca: boolean,
  reloadSignal = 0,
): UseWalletMovementsResult {
  const [searchParams] = useSearchParams();
  const [initial] = useState(() => readMovementsUrl(searchParams));
  const [filters, setFiltersState] = useState<WalletMovementsFilters>(initial.filters);
  const [page, setPageState] = useState(initial.page);
  // The header's "Incluir Coloca" changes the list like a filter does: back to the first page.
  const [lastIncludeColoca, setLastIncludeColoca] = useState(includeColoca);
  if (lastIncludeColoca !== includeColoca) {
    setLastIncludeColoca(includeColoca);
    setPageState(1);
  }

  useUrlParamMirror(MOVEMENTS_URL_PARAMS.search, filters.search);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.organizationId, filters.organizationId);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.currency, filters.currency);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.rail, filters.rail);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.direction, filters.direction);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.operationStatus, filters.operationStatus);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.incident, filters.incident);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.from, filters.from);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.to, filters.to);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.minAmount, filters.minAmount);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.maxAmount, filters.maxAmount);
  useUrlParamMirror(MOVEMENTS_URL_PARAMS.page, page > 1 ? String(page) : null);

  const search = useDebouncedValue(filters.search, TYPING_DEBOUNCE_MS);
  const minAmount = useDebouncedValue(filters.minAmount, TYPING_DEBOUNCE_MS);
  const maxAmount = useDebouncedValue(filters.maxAmount, TYPING_DEBOUNCE_MS);
  const [hasAccess, setHasAccess] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  // Keyed by its JSON: a keystroke makes a new filters object with the same debounced values, and a query that
  // changed identity with it would fire one request per keystroke.
  const queryJson = JSON.stringify(
    movementsQuery(
      { ...filters, search, minAmount, maxAmount },
      {
        excludeRoot: !includeColoca,
        skip: (page - 1) * WALLET_MOVEMENTS_PAGE_SIZE,
        limit: WALLET_MOVEMENTS_PAGE_SIZE,
      },
    ),
  );
  const query = useMemo(() => JSON.parse(queryJson) as ListPaymentMovementsParams, [queryJson]);
  const key = `${reloadKey}|${reloadSignal}|${queryJson}`;
  const [state, setState] = useState<{
    key: string | null;
    rows: PaymentMovement[];
    total: number;
    error: string | null;
  }>({ key: null, rows: [], total: 0, error: null });
  const seq = useRef(0);

  useEffect(() => {
    if (!enabled || !hasAccess) return;
    const current = ++seq.current;
    PaymentsWalletsService.movements(query)
      .then((res) => {
        if (current === seq.current) {
          setState({ key, rows: res.data.data, total: res.data.total, error: null });
        }
      })
      .catch((err: unknown) => {
        if (current !== seq.current) return;
        if (HttpApiService.isAccessDeniedError(err)) {
          setHasAccess(false);
          return;
        }
        setState((previous) => ({
          key,
          rows: previous.rows,
          total: previous.total,
          error: HttpApiService.getErrorMessage(err),
        }));
      });
  }, [enabled, hasAccess, key, query]);

  const setFilters = useCallback((patch: Partial<WalletMovementsFilters>) => {
    setFiltersState((current) => ({ ...current, ...patch }));
    setPageState(1);
  }, []);
  const replaceFilters = useCallback((next: WalletMovementsFilters) => {
    setFiltersState(next);
    setPageState(1);
  }, []);
  const resetFilters = useCallback(() => replaceFilters(EMPTY_MOVEMENTS_FILTERS), [replaceFilters]);
  const hasActiveFilters = useMemo(
    () =>
      (Object.keys(EMPTY_MOVEMENTS_FILTERS) as (keyof WalletMovementsFilters)[]).some(
        (name) => filters[name] !== EMPTY_MOVEMENTS_FILTERS[name],
      ),
    [filters],
  );
  const refresh = useCallback(() => setReloadKey((value) => value + 1), []);

  return {
    filters,
    setFilters,
    replaceFilters,
    resetFilters,
    hasActiveFilters,
    page,
    setPage: setPageState,
    rows: state.rows,
    total: state.total,
    isLoading: enabled && hasAccess && state.key !== key,
    isLoaded: state.key !== null,
    error: state.error,
    hasAccess,
    refresh,
  };
}
